import {SOURCE_WEATHERFLOW} from '@smartmixin/core';

export const migrations = {
  1: (state) => {
    return {
      ...state,
      prefs: {
        ...state.prefs,
        smallDecimals: true,
      },
    };
  },
  2: (state) => {
    return {
      ...state,
      prefs: {
        ...state.prefs,
        windDirectionConvention: 'oceanographic',
      },
    };
  },
  3: (state) => {
    const {stationDetailList, ...weather} = state.weather;
    return {...state, weather};
  },
  4: (state) => {
    return {
      ...state,
      prefs: {
        ...state.prefs,
        lightningLayer: true,
      },
    };
  },
  // Mirror of mobile's migration 31. `user_data` reached the backend on
  // 2023-03-13 and nothing ever normalised the persisted `link`, so a station
  // stored before then rehydrates with a link object that has no such key —
  // which crashed the station list on mobile (SMARTMIXIN-IOS-6R). The webapp
  // persists `weather` to localStorage under the same shape and had no
  // equivalent heal, so the 2023 shape would have lived there indefinitely.
  // The read sites are guarded now; this stops the shape propagating. `{}` is
  // what the backend stores for a station with no user overrides.
  5: (state) => {
    const stationList = state.weather?.stationList;
    if (!Array.isArray(stationList)) {
      return state;
    }
    return {
      ...state,
      weather: {
        ...state.weather,
        stationList: stationList.map((station) =>
          station?.link && !station.link.user_data
            ? {...station, link: {...station.link, user_data: {}}}
            : station,
        ),
      },
    };
  },
  // #190: `uiInteraction` (`alert`, `upgradeRequired`) was written to storage
  // from the 2024-02-01 (e1e72b399) rename until the blacklist was corrected
  // alongside this migration. Blacklisting is a WRITE-path filter only --
  // `passWhitelistBlacklist` is consulted in createPersistoid and nowhere in
  // getStoredState or persistReducer -- so the stored blob would still be
  // handed to the state reconciler one last time. This strips the key instead,
  // so no stale ERROR toast replays on the first load after the fix.
  6: (state) => {
    const {uiInteraction, ...rest} = state;
    return rest;
  },
  // #189: `SOURCE_WEATHERFLOW` was appended to the shared
  // `stationMapFilter.source` default in
  // `packages/core/src/store/weather/index.ts` by 0c4ca073f (2024-04-15) with
  // no migration on either app. `initialState` is not what an existing install
  // reads: `autoMergeLevel2` replaces `weather.stationMapFilter` wholesale from
  // storage because it sits at depth 2, and `getStationMapSourceFilter` only
  // falls back to DEFAULT_MAP_SOURCE_FILTER when the stored value is *absent*.
  // A seven-element array is truthy, so it is returned verbatim and
  // `WeatherStationOverlay` filters Weatherflow off the map permanently.
  //
  // Weatherflow only, unlike mobile's migration 33 -- but for a different
  // reason than mobile's. The webapp has NO filter UI: `setStationMapSource` is
  // dispatched from exactly two places and both are mobile
  // (FilterContainer.tsx, StationFilter.tsx). A webapp user cannot uncheck a
  // source, so this value is always whatever the default was when the blob was
  // written, and full reconciliation against the default would in fact be safe
  // here. It simply is not needed: this store first shipped 2024-01-15
  // (0882b28de), by which point Davis and Wunderground had been in the default
  // for over two years, so every webapp blob ever written already carried them.
  // Weatherflow is genuinely the only gap, and the affected window is blobs
  // written between 2024-01-15 and 2024-04-15.
  7: (state) => {
    const filter = state.weather && state.weather.stationMapFilter;

    // An absent filter is left absent: the selector's `|| DEFAULT` fallback
    // already answers correctly for that shape.
    if (!filter || !Array.isArray(filter.source)) {
      return state;
    }

    if (filter.source.includes(SOURCE_WEATHERFLOW)) {
      return state;
    }

    return {
      ...state,
      weather: {
        ...state.weather,
        stationMapFilter: {
          ...filter,
          source: filter.source.concat([SOURCE_WEATHERFLOW]),
        },
      },
    };
  },
  // NAM left the catalogue when RRFS replaced it (NOAA retires NAM
  // 2026-10-14). A per-station override stores a CATALOGUE key, and
  // `resolveSelectedForecastModel` returns the `find` result with no fallback
  // on that branch — so an override still pointing at NAM resolves to
  // `undefined` and the station shows no model until the user re-picks one.
  // Dropping it restores the normal default. Mirrors mobile migration 35.
  // Pins need no equivalent: `reorderKnownPins` deliberately keeps an unknown
  // pin so it survives a build that cannot draw it.
  8: (state) => {
    const drop = (overrides?: Record<string, string>) =>
      Object.fromEntries(
        Object.entries(overrides ?? {}).filter(
          ([, model]) => model !== 'NAM_CONUS' && model !== 'NAM_NORTH_AMERICA',
        ),
      );
    return {
      ...state,
      weather: {
        ...state.weather,
        selectedForecastModel: drop(state.weather?.selectedForecastModel),
      },
      location: {
        ...state.location,
        selectedForecastModel: drop(state.location?.selectedForecastModel),
      },
    };
  },
};
